/**
 * User and RBAC Types
 * 
 * Role-based access control with room-scoped permissions.
 * Integrated with Elevia Lab ecosystem.
 */

// User roles with increasing permissions
export type UserRole = 'viewer' | 'operator' | 'technician' | 'admin' | 'owner';

// Permission categories
export type PermissionCategory = 
  | 'devices'      // Control devices
  | 'rules'        // Manage automation rules
  | 'sensors'      // View/calibrate sensors
  | 'settings'     // Change controller settings
  | 'users'        // Manage users
  | 'ai'           // AI configuration
  | 'logs'         // View system logs
  | 'analytics'    // View analytics
  | 'backup'       // Backup/restore
  | 'hardware'     // Hardware configuration
  | 'irrigation'   // Irrigation dashboards, valves, programs
  | 'cultivation'  // Batches and cultivation workflows
  | 'rooms'        // Room management
  | 'alerts';      // Alert management

// Permission actions
export type PermissionAction = 'view' | 'control' | 'edit' | 'create' | 'delete' | 'admin';

// Full permission definition
export interface Permission {
  category: PermissionCategory;
  action: PermissionAction;
  roomScope?: string[]; // Empty = all rooms, specific IDs = limited
}

// Role permission matrix
export const rolePermissions: Record<UserRole, Permission[]> = {
  viewer: [
    { category: 'devices', action: 'view' },
    { category: 'sensors', action: 'view' },
    { category: 'analytics', action: 'view' },
    { category: 'irrigation', action: 'view' },
    { category: 'alerts', action: 'view' },
    { category: 'rooms', action: 'view' },
  ],
  operator: [
    { category: 'devices', action: 'view' },
    { category: 'devices', action: 'control' },
    { category: 'sensors', action: 'view' },
    { category: 'rules', action: 'view' },
    { category: 'analytics', action: 'view' },
    { category: 'logs', action: 'view' },
    { category: 'irrigation', action: 'view' },
    { category: 'irrigation', action: 'control' },
    { category: 'alerts', action: 'view' },
    { category: 'rooms', action: 'view' },
  ],
  technician: [
    { category: 'devices', action: 'view' },
    { category: 'devices', action: 'control' },
    { category: 'devices', action: 'edit' },
    { category: 'sensors', action: 'view' },
    { category: 'sensors', action: 'edit' },
    { category: 'rules', action: 'view' },
    { category: 'rules', action: 'edit' },
    { category: 'rules', action: 'create' },
    { category: 'analytics', action: 'view' },
    { category: 'logs', action: 'view' },
    { category: 'hardware', action: 'view' },
    { category: 'irrigation', action: 'view' },
    { category: 'irrigation', action: 'control' },
    { category: 'irrigation', action: 'edit' },
    { category: 'alerts', action: 'view' },
    { category: 'rooms', action: 'view' },
    { category: 'cultivation', action: 'view' },
  ],
  admin: [
    { category: 'devices', action: 'admin' },
    { category: 'sensors', action: 'admin' },
    { category: 'rules', action: 'admin' },
    { category: 'settings', action: 'edit' },
    { category: 'users', action: 'view' },
    { category: 'users', action: 'edit' },
    { category: 'users', action: 'create' },
    { category: 'users', action: 'delete' },
    { category: 'ai', action: 'edit' },
    { category: 'analytics', action: 'view' },
    { category: 'logs', action: 'view' },
    { category: 'backup', action: 'view' },
    { category: 'hardware', action: 'edit' },
    { category: 'irrigation', action: 'admin' },
    { category: 'cultivation', action: 'view' },
    { category: 'alerts', action: 'view' },
    { category: 'rooms', action: 'view' },
  ],
  owner: [
    { category: 'devices', action: 'admin' },
    { category: 'sensors', action: 'admin' },
    { category: 'rules', action: 'admin' },
    { category: 'settings', action: 'admin' },
    { category: 'users', action: 'admin' },
    { category: 'ai', action: 'admin' },
    { category: 'analytics', action: 'admin' },
    { category: 'logs', action: 'admin' },
    { category: 'backup', action: 'admin' },
    { category: 'hardware', action: 'admin' },
    { category: 'irrigation', action: 'admin' },
    { category: 'cultivation', action: 'admin' },
    { category: 'alerts', action: 'admin' },
    { category: 'rooms', action: 'admin' },
  ],
};

/**
 * Elevia User - Shared across Elevia Lab ecosystem
 */
export interface EleviaUser {
  // Core identity (shared across all Elevia apps)
  id: string;
  email: string;
  name: string;
  avatar?: string;
  
  // Authentication
  authProvider: 'email' | 'google' | 'microsoft';
  emailVerified: boolean;
  mfaEnabled: boolean;
  lastLogin: Date;
  
  // Organization
  organizationId: string;
  organizationName: string;
  
  // Controller-specific
  controllerAccess: ControllerAccess[];
  
  // Elevia ecosystem links
  eleviaApps: {
    inventory?: { userId: string; role: string };
    sales?: { userId: string; role: string };
    ecommerce?: { userId: string; role: string };
    employees?: { userId: string; role: string };
    controller?: { userId: string; role: UserRole };
  };
  
  // Preferences
  preferences: UserPreferences;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Controller-specific access
 */
export interface ControllerAccess {
  controllerId: string;
  controllerName: string;
  role: UserRole;
  roomAccess: RoomAccess[];
  grantedBy: string;
  grantedAt: Date;
  expiresAt?: Date;
}

/**
 * Room-scoped access
 */
export interface RoomAccess {
  roomId: string;
  roomName: string;
  permissions: Permission[];
}

/**
 * User preferences
 */
export interface UserPreferences {
  // Display
  theme: 'dark' | 'light' | 'system';
  temperatureUnit: 'celsius' | 'fahrenheit';
  language: string;
  timezone: string;
  
  // Notifications
  notifications: {
    push: boolean;
    email: boolean;
    sms: boolean;
    quietHoursStart?: string; // HH:MM
    quietHoursEnd?: string;
  };
  
  // Alert preferences
  alerts: {
    critical: boolean;
    warning: boolean;
    info: boolean;
    aiSuggestions: boolean;
  };

  // Display chrome (per-user; missing ⇒ shown)
  showFloatingAiChat?: boolean;

  // Dashboard
  defaultRoom?: string;
  dashboardLayout?: string;
}

/**
 * Audit log entry for user actions
 */
export interface AuditLogEntry {
  id: string;
  timestamp: Date;
  
  // Who
  userId: string;
  userName: string;
  userRole: UserRole;
  
  // What
  action: string;
  category: PermissionCategory;
  targetType: 'device' | 'sensor' | 'rule' | 'room' | 'user' | 'setting' | 'system';
  targetId: string;
  targetName: string;
  
  // Details
  changes?: {
    field: string;
    oldValue: unknown;
    newValue: unknown;
  }[];
  
  // Context
  controllerId: string;
  roomId?: string;
  ipAddress?: string;
  userAgent?: string;
}

/**
 * Session information
 */
export interface UserSession {
  id: string;
  userId: string;
  token: string;
  
  // Device info
  deviceType: 'desktop' | 'mobile' | 'tablet';
  browser: string;
  os: string;
  ipAddress: string;
  
  // Timestamps
  createdAt: Date;
  expiresAt: Date;
  lastActivityAt: Date;
  
  // Status
  isActive: boolean;
  revokedAt?: Date;
  revokedBy?: string;
}

// Helper functions

/** Role hierarchy for escalation checks */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  viewer: 1,
  operator: 2,
  technician: 3,
  admin: 4,
  owner: 5,
};

/**
 * Get controller access entry for a specific controller
 */
export function getControllerAccess(
  user: EleviaUser,
  controllerId: string
): ControllerAccess | null {
  const access = user.controllerAccess?.find(a => a.controllerId === controllerId);
  if (!access) return null;
  if (access.expiresAt && new Date(access.expiresAt) < new Date()) return null;
  return access;
}

/**
 * Whether the user can access all rooms on this controller
 */
export function canAccessAllRooms(user: EleviaUser, controllerId: string): boolean {
  const access = getControllerAccess(user, controllerId);
  if (!access) return false;
  return access.role === 'owner' || access.role === 'admin';
}

/**
 * Check room-level access (fail-closed for scoped roles without explicit rooms)
 */
export function hasRoomAccess(
  user: EleviaUser,
  controllerId: string,
  roomId: string
): boolean {
  const access = getControllerAccess(user, controllerId);
  if (!access) return false;
  if (canAccessAllRooms(user, controllerId)) return true;
  if (access.roomAccess.length === 0) return false;
  return access.roomAccess.some(r => r.roomId === roomId);
}

/**
 * Check if user has permission
 */
export function hasPermission(
  user: EleviaUser,
  controllerId: string,
  category: PermissionCategory,
  action: PermissionAction,
  roomId?: string
): boolean {
  const access = getControllerAccess(user, controllerId);
  if (!access) return false;

  const rolePerms = rolePermissions[access.role];

  // Higher actions satisfy lower ones within the same category
  const satisfies: Record<PermissionAction, PermissionAction[]> = {
    view: ['view', 'control', 'edit', 'create', 'delete', 'admin'],
    control: ['control', 'edit', 'admin'],
    edit: ['edit', 'create', 'delete', 'admin'],
    create: ['create', 'admin'],
    delete: ['delete', 'admin'],
    admin: ['admin'],
  };

  const hasRolePerm = rolePerms.some(p =>
    p.category === category &&
    satisfies[action].includes(p.action)
  );

  if (!hasRolePerm) return false;

  if (roomId) {
    return hasRoomAccess(user, controllerId, roomId);
  }

  return true;
}

/**
 * Get user's role for a controller
 */
export function getUserRole(user: EleviaUser, controllerId: string): UserRole | null {
  const access = getControllerAccess(user, controllerId);
  return access?.role ?? null;
}

/**
 * Get rooms user can access. Empty array means all rooms (owner/admin only).
 */
export function getAccessibleRooms(user: EleviaUser, controllerId: string): string[] {
  const access = getControllerAccess(user, controllerId);
  if (!access) return [];
  if (canAccessAllRooms(user, controllerId)) return [];
  return access.roomAccess.map(r => r.roomId);
}

/**
 * Whether caller can assign a target role (cannot assign above own role)
 */
export function canAssignRole(callerRole: UserRole, targetRole: UserRole): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'admin') return targetRole !== 'owner';
  return false;
}

/**
 * Whether caller can modify a user with the given role
 */
export function canModifyUser(callerRole: UserRole, targetRole: UserRole): boolean {
  if (callerRole === 'owner') return true;
  if (callerRole === 'admin') return targetRole !== 'owner' && targetRole !== 'admin';
  return false;
}

/** Count owners with access to a specific controller */
export function countOwnersForController(
  users: Array<{ id?: string; controllerAccess?: ControllerAccess[] }>,
  controllerId: string
): number {
  return users.filter(u =>
    u.controllerAccess?.some(
      a => a.controllerId === controllerId && a.role === 'owner'
    )
  ).length;
}

/** True when target user is the sole owner for a controller */
export function isLastOwnerForController(
  users: Array<{ id?: string; controllerAccess?: ControllerAccess[] }>,
  controllerId: string,
  targetUserId: string
): boolean {
  const owners = users.filter(u =>
    u.controllerAccess?.some(
      a => a.controllerId === controllerId && a.role === 'owner'
    )
  );
  return owners.length === 1 && owners[0].id === targetUserId;
}
